import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import { CustomerSupportRouteConstants } from 'AllComponents';
import { initiateTokenRefresh, toggleRefreshSpinner, TokenRefreshLocalKey, TokenRefreshSessionKeys } from 'Core/ducks/refreshToken';
import { AsyncResult, fromResult, Loading, LoadingAdditional } from 'Core/models/Async';
import { Page } from 'Core/models/Page';
import { setRefreshReRouteOptions } from 'Core/refreshTokenUtilityMethods';
import * as E from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { takeEvery } from 'redux-saga/effects';
import { Scope } from 'Shared/constants/Scope';
import { mergeWithNextPage } from 'Shared/utilities/PageUtility';
import customerSupportApi from 'Users/api/customerSupportApi';
import userApi from 'Users/api/userApi';
import {
  changeCustomerSupport,
  CustomerSupportActionTypes,
  impersonateCustomerSupportUser,
  searchCustomerUsers,
  stopCustomerSupportImpersonation,
} from 'Users/ducks/CustomerSupport';
import { UserModel } from 'Users/models/UserModel';
import { extractApplicationIdsFromScopeSaga } from './applicationIdSaga';

function* watchCustomerSupportUsersSaga() {
  function* getCustomerSupportFromApi(action: ReturnType<typeof searchCustomerUsers>) {
    const {filter, pageOption, encryptedIdentifier, recruiterCodes} = action.payload;
    const agentApplicationIds = yield extractApplicationIdsFromScopeSaga(Scope.AgentSearchCustomerSupport);
    const applicationIds = agentApplicationIds[0];

    if (action.payload.pageOption) {
      action.payload.pageOption.links.forEach(x => {
        if (!x.href.includes('api/user')) {
          x.href = 'api/user' + x.href;
        }
      });
      yield put(changeCustomerSupport(new LoadingAdditional(action.payload.pageOption)));
    } else {
      yield put(changeCustomerSupport(new Loading(action.payload.pageOption)));
    }
    const result: AsyncResult<Page<UserModel>> = yield pageOption
      ? call(mergeWithNextPage, {existingPage: pageOption})
      : call(
          customerSupportApi.searchCustomerSupportUsers,
          applicationIds,
          filter,
          encryptedIdentifier,
          recruiterCodes
        );

    yield pipe(
      result,
      E.map(fromResult),
      E.fold(
        value => put(changeCustomerSupport(value)),
        value => put(changeCustomerSupport(value))
      )
    );
  }

  yield takeLatest(CustomerSupportActionTypes.SEARCH_CUSTOMER_SUPPORTS, getCustomerSupportFromApi);
}

function* impersonateCustomerSupportUserSaga(action: ReturnType<typeof impersonateCustomerSupportUser>) {
  yield put(
    toggleRefreshSpinner(
      true,
      `Emulating ${action.payload.extraNameDetails[0]} ${action.payload.extraNameDetails[1]} (${action.payload.impersonatedCustomerSupportUsername})`
    )
  );
  yield call(userApi.impersonate, action.payload.username, action.payload.impersonatedCustomerSupportUsername);
  localStorage.setItem(TokenRefreshLocalKey.IN_EMULATION, 'true');
  setRefreshReRouteOptions(true);
  yield put(initiateTokenRefresh());
}

function* watchImpersonateCustomerSupportUserSaga() {
  yield takeLatest(CustomerSupportActionTypes.START_IMPERSONATE_CUSTOMER_SUPPORT, impersonateCustomerSupportUserSaga);
}

export function* stopImpersonationApi(action: ReturnType<typeof stopCustomerSupportImpersonation>) {
  yield put(toggleRefreshSpinner(true, `Returning to Customer Support...`));
  yield call(userApi.stopImpersonation, action.payload.username);
  localStorage.setItem(TokenRefreshLocalKey.IN_EMULATION, 'false');
  sessionStorage.removeItem(TokenRefreshSessionKeys.SAVED_ENCRYPTED_ID);
  setRefreshReRouteOptions(true, CustomerSupportRouteConstants.CUSTOMER_SUPPORT);
  yield put(initiateTokenRefresh());
}

export function* stopImpersonationSaga() {
  yield takeEvery(CustomerSupportActionTypes.STOP_IMPERSONATION, stopImpersonationApi);
}

export default function*() {
  yield all([watchCustomerSupportUsersSaga(), watchImpersonateCustomerSupportUserSaga(), stopImpersonationSaga()]);
}
